import { Icon, Tooltip } from '@dixa/lib-shared-habla';
import {
  formatInternational,
  getCountryCodeFromPhoneNumber,
  getPhoneNumberCountryCodeFormatted,
} from 'lib-shared-phone-numbers';
import _filter from 'lodash/filter';
import _orderBy from 'lodash/orderBy';
import { useCallback, useEffect, useRef, useState } from 'react';
import Collapsible from 'react-collapsible';
import { useSelector } from 'react-redux';

import {
  numbersApi,
  PhoneNumber,
  useLazyReadNumbersPaginatedQuery,
} from 'apis/numbers';
import { RootReducerStateT } from 'app/reducers/rootReducer';
import { useAppDispatch } from 'utils/redux/useAppDispatch';

import { actions } from '../../../telephony/actions/telephony';

import ListNumbers from './ListNumbers';
import { PhoneNumbersCustomInput } from './PhoneNumbersCustomInput';

import '../styles/from-number-selector.less';

type PropsT = {
  fromNumberFormat: string | null;
  isOwnNumbersViewOpen: boolean;

  toggleOwnNumbersView: (isOpen: boolean) => void;
  setFromNumberCountryCode: (countryCode: string | null) => void;
  returnFromNumberFormat: (fromNumberFormat: string | null) => void;
};

export const OutboundNumberSelector = ({
  fromNumberFormat,
  isOwnNumbersViewOpen,
  toggleOwnNumbersView,
  setFromNumberCountryCode,
  returnFromNumberFormat,
}: PropsT): JSX.Element => {
  const dispatch = useAppDispatch();

  const selectedFromNumber = useSelector(
    (state: RootReducerStateT) => state.telephony.selectedFromNumber,
  );
  const agentDefaultNumber = useSelector(
    (state: RootReducerStateT) =>
      state.settings.profile.profile.default_outbound_number,
  );

  const [fromNumberRegion, setFromNumberRegion] = useState('Unknown');

  // Eagerly fetches the full (unpaginated) number list so we can resolve the
  // agent's default outbound number and match country codes without waiting
  // for the user to open the dropdown. The dynamic paginated query below
  // drives the browsable/searchable list shown in the UI.
  const {
    staticTelephoneNumbersArray,
    isStaticLoading,
    staticNumbersResponse,
  } = numbersApi.useReadNumbersQuery(
    { limit: 25 },
    {
      selectFromResult: ({ data, isLoading }) => ({
        staticTelephoneNumbersArray: data?.data?.numbers ?? [],
        isStaticLoading: isLoading,
        staticNumbersResponse: data,
      }),
    },
  );

  useEffect(() => {
    if (staticNumbersResponse) {
      dispatch(
        actions.telephonyGetAllOwnNumbersSucceeded(staticNumbersResponse),
      );
    }
  }, [staticNumbersResponse, dispatch]);

  numbersApi.useReadDefaultNumberQuery();

  const {
    items: dynamicTelephoneNumbersArray,
    asyncStatus: dynamicAsyncStatus,
    hasNextPage,
    hasPreviousPage,
    nextPage,
    previousPage,
    changeArg,
  } = useLazyReadNumbersPaginatedQuery();

  useEffect(() => {
    // Always run on mount (asyncStatus is 'uninitialized') to populate the list
    // before the panel is opened. Re-run when the panel opens to refresh the list.
    // Intentionally skip when the panel closes to avoid an unnecessary re-fetch.
    if (dynamicAsyncStatus === 'uninitialized' || isOwnNumbersViewOpen) {
      changeArg({ limit: 15, searchTerm: undefined });
    }
    // dynamicAsyncStatus is intentionally excluded from deps — it is only used as
    // a one-time "has this ever fetched?" guard, not to react to every status change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOwnNumbersViewOpen, changeArg]);

  useEffect(() => {
    if (dynamicAsyncStatus === 'fulfilled') {
      dispatch(
        actions.telephonyGetQueryNumbersSucceeded({
          data: { numbers: dynamicTelephoneNumbersArray },
          meta: {
            next_page: hasNextPage ? 'next' : null,
            prev_page: hasPreviousPage ? 'prev' : null,
          },
        }),
      );
    }
  }, [
    dynamicTelephoneNumbersArray,
    dynamicAsyncStatus,
    hasNextPage,
    hasPreviousPage,
    dispatch,
  ]);

  const orderedDynamicNumbers = _orderBy(dynamicTelephoneNumbersArray, [
    'e164_format',
  ]);

  const changeFromNumber = useCallback(
    (fromNumber: PhoneNumber | null = null) => {
      if (fromNumber === null) {
        return;
      }
      dispatch(actions.telephonyChangeFromNumber({ number: fromNumber }));
      const fromPhoneNumberFormat =
        formatInternational(fromNumber.e164_format) || fromNumber.e164_format;
      const region =
        getCountryCodeFromPhoneNumber(fromNumber.e164_format)?.toUpperCase() ||
        'Unknown';
      const countryCode =
        getPhoneNumberCountryCodeFormatted(fromNumber.e164_format) || null;
      setFromNumberCountryCode(countryCode);
      returnFromNumberFormat(fromPhoneNumberFormat);
      setFromNumberRegion(region);
    },
    [dispatch, setFromNumberCountryCode, returnFromNumberFormat],
  );

  const findNumberByCountryCode = useCallback(
    (number: string | null = null): PhoneNumber | null => {
      if (staticTelephoneNumbersArray.length === 0) {
        console.warn(
          '_findSameOwnNumberWithSameCountryCode no owned number given.',
        );
        return null;
      }

      let verifiedNumbers: readonly PhoneNumber[] | null = null;

      if (agentDefaultNumber) {
        verifiedNumbers = _filter(staticTelephoneNumbersArray, {
          e164_format: agentDefaultNumber,
        });
      } else {
        verifiedNumbers = _filter(staticTelephoneNumbersArray, {
          default: true,
        });
      }

      if (!verifiedNumbers || verifiedNumbers.length === 0) {
        verifiedNumbers = [...staticTelephoneNumbersArray];
      }

      let foundNumber: PhoneNumber | null = null;
      if (number !== null) {
        // find country code on number and match it with a from number (if any).
        const countryCode = getPhoneNumberCountryCodeFormatted(number);
        for (const ownedNumber of verifiedNumbers) {
          const ownedCountryCode = getPhoneNumberCountryCodeFormatted(
            ownedNumber.e164_format,
          );

          if (countryCode === ownedCountryCode) {
            foundNumber = ownedNumber;
            break;
          }
        }
      }
      return foundNumber;
    },
    [staticTelephoneNumbersArray, agentDefaultNumber],
  );

  const prevSelectedFromNumberRef = useRef(selectedFromNumber);
  useEffect(() => {
    let verifiedNumbers: readonly PhoneNumber[] | null = null;

    if (agentDefaultNumber) {
      verifiedNumbers = _filter(staticTelephoneNumbersArray, {
        e164_format: agentDefaultNumber,
      });
    } else {
      verifiedNumbers = _filter(staticTelephoneNumbersArray, {
        default: true,
      });
    }

    if (!verifiedNumbers || verifiedNumbers.length === 0) {
      verifiedNumbers = _filter(staticTelephoneNumbersArray, {
        outgoing_functionality: true,
      });
    }

    if (selectedFromNumber === null && verifiedNumbers.length > 0) {
      let outboundNumber = '';
      if (fromNumberFormat) {
        outboundNumber = fromNumberFormat;
      } else {
        outboundNumber = '+4560860931';
      }
      const fromNumber = findNumberByCountryCode(outboundNumber); // @TODO make a default counrty for each organization.

      if (fromNumber !== null) {
        changeFromNumber(fromNumber);
      } else {
        changeFromNumber(verifiedNumbers[0]);
      }
    }

    if (prevSelectedFromNumberRef.current !== selectedFromNumber) {
      changeFromNumber(selectedFromNumber);
    }
    prevSelectedFromNumberRef.current = selectedFromNumber;
  }, [
    staticTelephoneNumbersArray,
    selectedFromNumber,
    agentDefaultNumber,
    fromNumberFormat,
    findNumberByCountryCode,
    changeFromNumber,
  ]);

  const onOwnNumberToggle = useCallback(() => {
    toggleOwnNumbersView(!isOwnNumbersViewOpen);
  }, [toggleOwnNumbersView, isOwnNumbersViewOpen]);

  const onSearch = useCallback(
    (searchTerm: string) => {
      changeArg(
        { searchTerm: searchTerm || undefined, limit: 15 },
        { debounceTime: 300 },
      );
    },
    [changeArg],
  );

  const staticLoaded = !isStaticLoading;

  let fromNumber: JSX.Element | null = null;
  let disabledPrevious = '';
  let disabledNext = '';
  let isHidden = '';
  let buttonsHidden = '';

  if (
    staticLoaded &&
    staticTelephoneNumbersArray.length > 0 &&
    dynamicAsyncStatus === 'fulfilled' &&
    orderedDynamicNumbers.length === 0
  ) {
    buttonsHidden = ' hidden';
  }

  if (selectedFromNumber && selectedFromNumber.name) {
    fromNumber = (
      <div className="call-from flags">
        <p className="m-b-5 noselect">Outbound number</p>
        <Tooltip content="Outbound call recording is active">
          <span
            className={
              'call-recording ' +
              (selectedFromNumber.record_outbound_calls === true
                ? ''
                : 'hidden')
            }
          >
            <Icon name="rec" />
          </span>
        </Tooltip>
        <span className={'flag ' + fromNumberRegion.toLowerCase()}></span>
        <Tooltip side="bottom" content={selectedFromNumber.name}>
          <span className="from-number-name">
            {fromNumberFormat} <em>({selectedFromNumber.name})</em>
          </span>
        </Tooltip>
        <Icon
          name="angle-right"
          size={20}
          className="Collapsible__trigger-icon"
        />
      </div>
    );
  } else if (selectedFromNumber) {
    fromNumber = (
      <div className="call-from flags">
        <p className="m-b-5 noselect">Outbound number</p>
        <Tooltip content="Outbound call recording is active">
          <span
            className={
              'call-recording ' +
              (selectedFromNumber.record_outbound_calls === true
                ? ''
                : 'hidden')
            }
          ></span>
        </Tooltip>
        <span className={'flag ' + fromNumberRegion.toLowerCase()}></span>
        <span>{fromNumberFormat}</span>
        <Icon
          name="angle-right"
          size={20}
          className="Collapsible__trigger-icon"
        />
      </div>
    );
  }

  if (!hasPreviousPage) {
    disabledPrevious = 'disabled';
  }

  if (!hasNextPage) {
    disabledNext = 'disabled';
  }

  if (staticLoaded && staticTelephoneNumbersArray.length === 0) {
    isHidden = ' hidden';
  }

  return (
    <div className={`collapsible-holder from-numbers-selector${isHidden}`}>
      <Collapsible
        handleTriggerClick={onOwnNumberToggle}
        open={isOwnNumbersViewOpen}
        trigger={fromNumber as any}
        transitionTime={300}
        easing="ease"
      >
        <li className="own-number-change dropdown-header" role="heading">
          Search outbound numbers
        </li>
        <PhoneNumbersCustomInput onSearch={onSearch} />
        <li
          className={`own-number-change${buttonsHidden} dropdown-header`}
          role="presentation"
        >
          Select outbound number
        </li>
        <ListNumbers
          dynamicTelephoneNumbersArray={orderedDynamicNumbers}
          onToggle={onOwnNumberToggle}
          selectedFromNumber={selectedFromNumber}
          changeFromNumber={changeFromNumber}
        />
        {dynamicAsyncStatus === 'pending' ? (
          <ul className={`pager${buttonsHidden}`} style={{ margin: 'auto' }}>
            <li className="disabled pull-left">
              <a type="button">
                {' '}
                <span style={{ verticalAlign: '-2px' }}>
                  <Icon name="arrow-left" />
                </span>
                Loading{' '}
              </a>
            </li>
            <li className="disabled pull-right">
              <a type="button">
                {' '}
                Loading{' '}
                <span style={{ verticalAlign: '-2px' }}>
                  {' '}
                  <Icon name="arrow-right" />{' '}
                </span>
              </a>
            </li>
          </ul>
        ) : (
          <ul className={`pager${buttonsHidden}`} style={{ margin: 'auto' }}>
            <li className={disabledPrevious + ' previous'}>
              {!hasPreviousPage ? (
                <a type="button">
                  <span style={{ verticalAlign: '-2px' }}>
                    <Icon name="arrow-left" />
                  </span>
                  Prev Page
                </a>
              ) : (
                <a
                  type="button"
                  style={{ cursor: 'pointer' }}
                  onClick={() => previousPage()}
                >
                  <span style={{ verticalAlign: '-2px' }}>
                    <Icon name="arrow-left" />
                  </span>
                  Prev Page
                </a>
              )}
            </li>
            <li className={disabledNext + ' next'}>
              {!hasNextPage ? (
                <a className={buttonsHidden} type="button">
                  Next page
                  <span style={{ verticalAlign: '-2px' }}>
                    <Icon name="arrow-right" />
                  </span>
                </a>
              ) : (
                <a
                  className={buttonsHidden}
                  type="button"
                  style={{ cursor: 'pointer' }}
                  onClick={() => nextPage()}
                >
                  Next page
                  <span style={{ verticalAlign: '-2px' }}>
                    <Icon name="arrow-right" />
                  </span>
                </a>
              )}
            </li>
          </ul>
        )}
      </Collapsible>
      <li
        className="divider"
        role="separator"
        style={{ margin: '13px 0 0 0', width: '100%', listStyleType: 'none' }}
      />
    </div>
  );
};
